import React from "react";
import { Stack } from "@mui/material";
import styled from "styled-components";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import TerminalIcon from "@mui/icons-material/Terminal";
import RateReviewIcon from "@mui/icons-material/RateReview";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import { secondsToHours, secondsToMinutes } from "date-fns";
import CustomTooltip from "src/components/custom-tooltip";

const SectionStack = styled(Stack)`
    border-right: 1px dashed #5a5a5a;
    align-items: flex-end;
    padding: 6px;
`;

const MainValue = styled.h2`
    font-size: 24px;
    line-height: 1;
    margin-bottom: 0;
    margin-top: 4px;
    cursor: pointer;
`;

const Title = styled.h2`
    font-size: 12px;
    font-weight: 400;
    margin-bottom: 0px;
`;

const Divider = styled.div`
    height: 12px;
    width: 100%;
    background-color: #545454;
`;

const HelperText = styled.p`
    font-size: 12px;
    font-weight: 200;
    opacity: 0.4;
    margin: 0;
`;
const AverageText = styled.p`
    font-size: 12px;
    margin: 0;
    font-weight: 300;
    color: ${({ theme, value }) => {
        if (value > 0) return theme.palette.primary.main;
        if (value < 0) return theme.palette.warning.dark;
        if (value === 0) return theme.palette.text.disabled;
        return theme.palette.text.disabled;
    }};
`;

const InProgressIcon = styled(TerminalIcon)`
    position: relative;
    top: 6px;
    margin-right: 6px;
    opacity: 0.3;
`;

const ReviewIcon = styled(RateReviewIcon)`
    position: relative;
    top: 6px;
    margin-right: 6px;
    opacity: 0.3;
`;

const MergeIcon = styled(CallMergeIcon)`
    position: relative;
    top: 6px;
    margin-right: 6px;
    opacity: 0.3;
`;

const TooltipTitleHead = styled.p`
    margin: 0 0 12px 0;
    font-size: 14px;
`;
const TooltipTitleBody = styled.p`
    margin: 0;
    font-weight: 300;
    font-size: 12px;
`;

const TooltipTitleBodyBold = styled.p`
    margin: 0;
    font-weight: 400;
    font-size: 12px;
`;

const AverageTextContent = "usually";

const TimeSplitter = (value, variant) => {
    if (variant === "chrono") {
        if (value === 0) return "-";
        if (value < 60) return value;
        if (value >= 60 && value <= 3600) return secondsToMinutes(value);
        if (value > 3600 && value < 86400) return secondsToHours(value);
        if (value >= 86400) return Math.round(secondsToHours(value) / 24);
    }
    if (variant === "text") {
        if (value === 0) return "No time spent";
        if (value < 60) return "seconds";
        if (value === 60) return "minute";
        if (value <= 3600) return "minutes";
        if (value > 3600 && value <= 86400) return "hours";
        if (value >= 86400 && value < 172800) return "day";
        if (value >= 172800) return "days";
    }
    if (variant === "minified") {
        if (value <= 0 && value > -3600) return `${secondsToMinutes(value)} m`;
        if (value <= -3600 && value > -86400) return `${secondsToHours(value)} m`;
        if (value <= -86400) return `${Math.round(secondsToHours(value) / 24)} d`;
        if (value <= 3600) return `${secondsToMinutes(value)} m`;
        if (value > 3600 && value < 86400) return `${secondsToHours(value)} h`;
        if (value >= 86400) return `${Math.round(secondsToHours(value) / 24)} d`;
    }
    return null;
};

const TooltipTitle = ({ avg, med, nineTeeth, ninetyFifth, ninetyNinth, name }) => {
    return (
        <Stack direction="column" spacing={1}>
            <TooltipTitleHead>{name} breakdown</TooltipTitleHead>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <TooltipTitleBody>Average:</TooltipTitleBody>
                <TooltipTitleBodyBold>
                    {TimeSplitter(avg, "chrono")} {TimeSplitter(avg, "text")}
                </TooltipTitleBodyBold>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <TooltipTitleBody>Median:</TooltipTitleBody>
                <TooltipTitleBodyBold>
                    {TimeSplitter(med, "chrono")} {TimeSplitter(med, "text")}
                </TooltipTitleBodyBold>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
                <TooltipTitleBody>90th percentile:</TooltipTitleBody>
                <TooltipTitleBodyBold>
                    {TimeSplitter(nineTeeth, "chrono")} {TimeSplitter(nineTeeth, "text")}
                </TooltipTitleBodyBold>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <TooltipTitleBody>95th percentile:</TooltipTitleBody>
                <TooltipTitleBodyBold>
                    {TimeSplitter(ninetyFifth, "chrono")} {TimeSplitter(ninetyFifth, "text")}
                </TooltipTitleBodyBold>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <TooltipTitleBody>99th percentile:</TooltipTitleBody>
                <TooltipTitleBodyBold>
                    {TimeSplitter(ninetyNinth, "chrono")} {TimeSplitter(ninetyNinth, "text")}
                </TooltipTitleBodyBold>
            </Stack>
        </Stack>
    );
};
const MrTimingStats = ({ data }) => {
    if (!data) return <NoDataPlaceholder variant="list" />;
    return (
        <>
            <Stack direction="row" spacing={0.5} pt={1} style={{ height: "85%", width: "100%" }}>
                <Stack style={{ width: "100%" }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        pr={1}
                        pb={0.5}
                    >
                        <Stack direction="row" alignItems="center">
                            <InProgressIcon />
                            <Title>In progress</Title>
                        </Stack>
                    </Stack>
                    <Divider />
                    <SectionStack>
                        <CustomTooltip
                            title={TooltipTitle({
                                avg: data.in_progress_duration_avg,
                                med: data.in_progress_duration_median,
                                nineTeeth: data.in_progress_duration_90th,
                                ninetyFifth: data.in_progress_duration_95th,
                                ninetyNinth: data.in_progress_duration_99th,
                                name: "In progress",
                            })}
                        >
                            <MainValue>
                                {TimeSplitter(data.in_progress_duration_adj_avg, "chrono")}
                            </MainValue>
                        </CustomTooltip>
                        <HelperText>
                            {TimeSplitter(data.in_progress_duration_adj_avg, "text")} (without
                            outliers)
                        </HelperText>
                        <Stack direction="row" alignItems="center" spacing={0.5} pt={0.5}>
                            <AverageText value={data.inProgressDelta}>
                                {TimeSplitter(
                                    data.baseline.in_progress_duration_adj_avg,
                                    "minified"
                                )}
                            </AverageText>
                            <AverageText value={data.inProgressDelta}>
                                {AverageTextContent}
                            </AverageText>
                        </Stack>
                    </SectionStack>
                </Stack>
                <Stack style={{ width: "100%" }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        pr={1}
                        pb={0.5}
                    >
                        <Stack direction="row" alignItems="center">
                            <ReviewIcon />
                            <Title>Under review</Title>
                        </Stack>
                    </Stack>
                    <Divider />
                    <SectionStack>
                        <CustomTooltip
                            title={TooltipTitle({
                                avg: data.under_review_duration_avg,
                                med: data.under_review_duration_median,
                                nineTeeth: data.under_review_duration_90th,
                                ninetyFifth: data.under_review_duration_95th,
                                ninetyNinth: data.under_review_duration_99th,
                                name: "Under review",
                            })}
                        >
                            <MainValue>
                                {TimeSplitter(data.under_review_duration_adj_avg, "chrono")}
                            </MainValue>
                        </CustomTooltip>
                        <HelperText>
                            {TimeSplitter(data.under_review_duration_adj_avg, "text")} (without
                            outliers)
                        </HelperText>
                        <Stack direction="row" alignItems="center" spacing={0.5} pt={0.5}>
                            <AverageText value={data.underReviewDelta}>
                                {TimeSplitter(
                                    data.baseline.under_review_duration_adj_avg,
                                    "minified"
                                )}
                            </AverageText>
                            <AverageText value={data.underReviewDelta}>
                                {AverageTextContent}
                            </AverageText>
                        </Stack>
                    </SectionStack>
                </Stack>
                <Stack style={{ width: "100%" }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        pr={1}
                        pb={0.5}
                    >
                        <Stack direction="row" alignItems="center">
                            <MergeIcon />
                            <Title>Approval to Merge</Title>
                        </Stack>
                    </Stack>
                    <Divider />
                    <SectionStack>
                        <CustomTooltip
                            title={TooltipTitle({
                                avg: data.approved_to_merge_duration_avg,
                                med: data.approved_to_merge_duration_median,
                                nineTeeth: data.approved_to_merge_duration_90th,
                                ninetyFifth: data.approved_to_merge_duration_95th,
                                ninetyNinth: data.approved_to_merge_duration_99th,
                                name: "Approval to Merge",
                            })}
                        >
                            <MainValue>
                                {TimeSplitter(data.approved_to_merge_duration_adj_avg, "chrono")}
                            </MainValue>
                        </CustomTooltip>
                        <HelperText>
                            {TimeSplitter(data.approved_to_merge_duration_adj_avg, "text")} (without
                            outliers)
                        </HelperText>
                        <Stack direction="row" alignItems="center" spacing={0.5} pt={0.5}>
                            <AverageText value={data.approvedToMergeDelta}>
                                {TimeSplitter(
                                    data.baseline.approved_to_merge_duration_adj_avg,
                                    "minified"
                                )}
                            </AverageText>
                            <AverageText value={data.approvedToMergeDelta}>
                                {AverageTextContent}
                            </AverageText>
                        </Stack>
                    </SectionStack>
                </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} mb={1}>
                <Title style={{ margin: 0 }}>Total MR Lead Time:</Title>
                <Stack direction="column" alignItems="flex-start" justifyContent="center">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <MainValue style={{ margin: 0, fontSize: "18px" }}>
                            {TimeSplitter(data.total_duration_adj_avg, "chrono")}
                        </MainValue>
                        <HelperText>{TimeSplitter(data.total_duration_adj_avg, "text")}</HelperText>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AverageText
                            style={{ padding: 0 }}
                            value={
                                data.total_duration_adj_avg - data.baseline.total_duration_adj_avg
                            }
                        >
                            {TimeSplitter(data.baseline.total_duration_adj_avg, "minified")}
                        </AverageText>
                        <AverageText
                            style={{ padding: 0 }}
                            value={
                                data.total_duration_adj_avg - data.baseline.total_duration_adj_avg
                            }
                        >
                            {AverageTextContent}
                        </AverageText>
                    </Stack>
                </Stack>
            </Stack>
        </>
    );
};

export default MrTimingStats;
